import { useId } from 'react'
import { data, Form, redirect } from 'react-router'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { OyButton } from '#app/components/oy/oy-button.tsx'
import { OyField, OySectionLabel } from '#app/components/oy/oy-field.tsx'
import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import { OYKA_SENDER_NAME, smsConfigured } from '#app/utils/sms.server.ts'
import { resolveStalViewer } from '#app/utils/stal-access.server.ts'
import { stableName } from '#app/utils/stal-viewer.server.ts'
import { type Route } from './+types/login.ts'
import {
	stalLoginRequest,
	stalLoginVerify,
	type StalLoginStep,
} from './login.server.ts'

/**
 * Herontwerp P6 — the Stal-app login (StalApp closed-door language): name
 * → code by text → the `oyka_stal` cookie. Chromeless and noindex; the
 * honeypot and the per-name / per-IP counters guard the paid step; the
 * "/login" substring puts the path under the server's strongest rate
 * limiter. Oyka never asks for a code by text and never sends a login
 * link that was not just promised on this screen (ADR-026 §8).
 */
export const handle = { hideAppChrome: true, getSitemapEntries: () => null }

export const meta: Route.MetaFunction = () => [
	{ title: 'Inloggen voor de stal in Oyka' },
	{ name: 'robots', content: 'noindex, nofollow' },
]

export async function loader({ request }: Route.LoaderArgs) {
	const viewer = await resolveStalViewer(request, null, new Date())
	if (viewer.ok) throw redirect('/stal')
	return data({
		stableName: await stableName(),
		smsOn: smsConfigured(),
		senderName: OYKA_SENDER_NAME,
	})
}

export async function action({ request }: Route.ActionArgs) {
	const form = await request.formData()
	await checkHoneypot(form)
	const intent = String(form.get('intent') ?? '')
	const name = String(form.get('naam') ?? '')
		.trim()
		.slice(0, 120)
	const now = new Date()
	if (intent === 'request') {
		const step = await stalLoginRequest(request, name, now)
		return data<StalLoginStep>(step, { status: step.error ? 400 : 200 })
	}
	if (intent === 'verify') {
		const res = await stalLoginVerify(name, String(form.get('code') ?? ''), now)
		if (!res.ok) return data<StalLoginStep>(res.step, { status: 400 })
		throw redirect('/stal', { headers: { 'set-cookie': res.setCookie } })
	}
	return data<StalLoginStep>({ step: 'name' }, { status: 400 })
}

export default function StalLoginPage({
	loaderData,
	actionData,
}: Route.ComponentProps) {
	const naamId = useId()
	const codeId = useId()
	const step = actionData?.step ?? 'name'
	const name = actionData?.step === 'code' ? actionData.name : ''
	const error = actionData?.error
	return (
		<main className="st-main">
			<div className="st-closed">
				<span className="oy-stal-brand" aria-hidden="true">
					🐴
				</span>
				<h1>{loaderData.stableName}</h1>
				{step === 'name' ? (
					<Form method="post">
						<HoneypotInputs />
						<input type="hidden" name="intent" value="request" />
						<p>
							Hoi! Log in met je naam. Je krijgt dan een code per sms op je
							eigen nummer.
						</p>
						<OySectionLabel as="label" htmlFor={naamId}>
							Je naam, zoals de stal je kent
						</OySectionLabel>
						<OyField
							id={naamId}
							name="naam"
							autoComplete="name"
							autoFocus
							required
							maxLength={120}
						/>
						{error ? <p className="err">{error}</p> : null}
						<OyButton type="submit">Stuur mij een code</OyButton>
						{!loaderData.smsOn ? (
							<p>
								Het inloggen per sms staat nog niet aan. Vraag om je
								persoonlijke link.
							</p>
						) : null}
					</Form>
				) : (
					<Form method="post">
						<HoneypotInputs />
						<input type="hidden" name="intent" value="verify" />
						<input type="hidden" name="naam" value={name} />
						<p>
							Als je naam bekend is bij de stal, krijg je zo een code per sms op
							je eigen nummer. Vul die hieronder in.
						</p>
						<OySectionLabel as="label" htmlFor={codeId}>
							Code uit het sms’je
						</OySectionLabel>
						<OyField
							id={codeId}
							name="code"
							inputMode="numeric"
							autoComplete="one-time-code"
							pattern="[0-9 ]*"
							placeholder="123 456"
							autoFocus
							required
						/>
						{error ? <p className="err">{error}</p> : null}
						<OyButton type="submit">Inloggen</OyButton>
						<p>
							Geen sms gekregen?{' '}
							<a href="/stal/login">Probeer het nog eens met je naam.</a>
						</p>
					</Form>
				)}
				<footer>
					Oyka vraagt nooit om je code per sms en stuurt nooit een inloglink die
					je niet net op dit scherm hebt aangevraagd.
					{loaderData.smsOn
						? ` Sla het nummer op als ${loaderData.senderName}, dan zie je altijd wie er schrijft.`
						: null}
				</footer>
			</div>
		</main>
	)
}
